import Vue from 'vue'
import VueRouter from 'vue-router'
import HomeView from '../views/HomeView.vue'
import AuthService from '@/services/auth'
import { hasExpired } from '@/utils/subscriptionHelper'
import store from '@/store'

Vue.use(VueRouter)

const routes = [
  // RUTAS PÚBLICAS (RAÍZ)
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/tienda',
    name: 'shop',
    component: () => import(/* webpackChunkName: "shop" */ '../views/ShopView.vue')
  },
  {
    path: '/producto/anillo',
    name: 'product-ring',
    component: () => import(/* webpackChunkName: "product-ring" */ '../views/ProductRingView.vue')
  },
  {
    path: '/producto/tarjeta',
    name: 'product-card',
    component: () => import(/* webpackChunkName: "product-card" */ '../views/ProductCardView.vue')
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import(/* webpackChunkName: "checkout" */ '../views/CheckoutView.vue')
    // No requiere auth - permite guest checkout
  },
  {
    path: '/checkout/success',
    name: 'checkout-success',
    component: () => import(/* webpackChunkName: "checkout-success" */ '../views/CheckoutSuccessView.vue')
    // Sin requiresAuth: Stripe regresa aquí también a invitados
  },
  {
    path: '/checkout/cancel',
    name: 'checkout-cancel',
    component: () => import(/* webpackChunkName: "checkout-cancel" */ '../views/CheckoutCancelView.vue')
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '../views/LoginView.vue')
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import(/* webpackChunkName: "reset-password" */ '../views/ResetPasswordView.vue'),
    meta: { hideHeader: true }
  },
  {
    path: '/setup-account',
    name: 'setup-account',
    component: () => import(/* webpackChunkName: "setup-account" */ '../views/SetupAccountView.vue'),
    meta: { hideHeader: true }
  },
  {
    path: '/instrucciones',
    name: 'instructions',
    component: () => import(/* webpackChunkName: "instructions" */ '../views/InstructionsView.vue')
  },
  {
    path: '/aviso-privacidad',
    name: 'privacy-notice',
    component: () => import(/* webpackChunkName: "privacy-notice" */ '../views/PrivacyNoticeView.vue')
  },
  {
    path: '/terminos-condiciones',
    name: 'terms-conditions',
    component: () => import(/* webpackChunkName: "terms-conditions" */ '../views/TermsConditionsView.vue')
  },
  {
    path: '/website-expired',
    name: 'website-expired',
    component: () => import(/* webpackChunkName: "website-expired" */ '../views/WebsiteExpiredView.vue'),
    meta: { hideHeader: true }
  },
  {
    path: '/form/:uuid',
    name: 'public-form',
    component: () => import(/* webpackChunkName: "public-form" */ '../views/PublicFormView.vue'),
    meta: { hideHeader: true }
  },
  {
    path: '/billing-expired',
    name: 'billing-expired',
    component: () => import(/* webpackChunkName: "billing-expired" */ '../views/BillingExpiredView.vue'),
    meta: { hideHeader: true }
  },

  // RUTAS PRIVADAS (DASHBOARD)
  {
    path: '/dashboard',
    component: { render: c => c('router-view') },
    meta: { requiresAuth: true },
    children: [
      {
        path: '', // /dashboard
        redirect: 'templates'
      },
      {
        path: 'templates',
        name: 'templates',
        component: () => import(/* webpackChunkName: "templates" */ '../views/TemplatesView.vue')
      },
      {
        path: 'create-nfcero-v2/:id?',
        name: 'CreateNFCeroV2',
        component: () => import('@/views/CreateNFCeroV2View.vue')
      },
      {
        path: 'create-nfcero/:id?',
        name: 'CreateNFCero',
        component: () => import('@/views/CreateNFCeroView.vue')
      },
      {
        path: 'analytics',
        name: 'analytics',
        component: () => import(/* webpackChunkName: "analytics" */ '../views/AnalyticsView.vue')
      },
      {
        path: 'form-data',
        name: 'form-data',
        component: () => import(/* webpackChunkName: "form-data" */ '../views/FormDataView.vue')
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import(/* webpackChunkName: "calendar" */ '../views/CalendarView.vue')
      },
      {
        path: 'file-manager',
        name: 'file-manager',
        component: () => import(/* webpackChunkName: "file-manager" */ '../views/FileManagerView.vue')
      },
      {
        path: 'extras',
        name: 'extras',
        component: () => import(/* webpackChunkName: "extras" */ '../views/ExtrasView.vue')
      },
      {
        path: 'support',
        name: 'support',
        component: () => import(/* webpackChunkName: "support" */ '../views/SupportView.vue')
      },
      {
        path: 'settings',
        component: () => import(/* webpackChunkName: "settings" */ '../views/SettingsView.vue'),
        children: [
          {
            path: '',
            redirect: 'profile'
          },
          {
            path: 'profile',
            name: 'settings-profile',
            component: () => import(/* webpackChunkName: "settings-profile" */ '../views/settings/ProfileView.vue'),
          },
          {
            path: 'billing',
            name: 'settings-billing',
            component: () => import(/* webpackChunkName: "settings-billing" */ '../views/settings/BillingView.vue'),
          },
          {
            path: 'password',
            name: 'settings-password',
            component: () => import(/* webpackChunkName: "settings-password" */ '../views/settings/PasswordView.vue'),
          }
        ]
      },
      {
        path: 'forms/:formId/submissions',
        name: 'form-submissions',
        component: () => import(/* webpackChunkName: "form-submissions" */ '../views/FormSubmissionsView.vue')
      }
    ]
  },

  // RUTA COMODÍN PARA SITIOS DE USUARIOS (AL FINAL)
  {
    path: '/:domain',
    name: 'website',
    component: () => import(/* webpackChunkName: "website" */ '../views/WebsiteView.vue'),
    meta: { hideHeader: true },
    beforeEnter: (to, from, next) => {
      const reservedRoutes = ['login', 'instrucciones', 'dashboard', 'static', 'img', 'css', 'js', 'setup-account', 'reset-password', 'form', 'tienda', 'producto', 'checkout']
      if (reservedRoutes.includes(to.params.domain)) {
        next({ name: 'home' })
      } else {
        next()
      }
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      // Auditoría N3: el enlace "suscripción" de los productos lleva directo a los planes
      return { selector: to.hash, offset: { x: 0, y: 70 } };
    } else {
      return { x: 0, y: 0 };
    }
  },
})

router.beforeEach((to, from, next) => {
  // ✅ /billing-expired está completamente pública, SIN VERIFICACIONES
  if (to.name === 'billing-expired') {
    next();
    return;
  }

  const isAuthenticated = AuthService.isAuthenticated();
  const isDashboardRoute = to.matched.some(record => record.path && record.path.includes('/dashboard'));
  const user = store.state.auth.user;

  // Si necesita autenticación
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      // No autenticado, ir a login
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      });
      return;
    }

    // Autenticado - verificar suscripción
    const subscriptionExpired = user && hasExpired(user);

    if (isDashboardRoute && subscriptionExpired) {
      // Dashboard pero expirado
      next({
        name: 'billing-expired'
      });
      return;
    }

    next();
  } else {
    // Ruta pública
    next();
  }
});

export default router